import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Skeleton } from "@mui/material";

import api from "@helpers/api";
import Parser from "@helpers/parser";
import { Text, Button } from "@fragments";
import { useCommunity } from "@contexts/CommunityContext";

import "./styles.scss";

function planInstallmentCents(plan) {
    if (plan.installments_list?.length && plan.installments > 0) {
        const last = plan.installments_list[plan.installments - 1];
        return Math.round(last / plan.installments);
    }
    return plan.price;
}

export default function CommunityPlansAvailableTable({ communityId, availability }) {
    const { t } = useTranslation("community");
    const { slug } = useParams();
    const { community: communityCtx } = useCommunity();

    const resolvedCommunity = slug ? communityCtx.getBySlug(slug) : undefined;
    const resolvedCommunityId = resolvedCommunity?.id ?? communityId;
    const checkoutSlug = resolvedCommunity?.checkout;

    const [allPlans, setAllPlans] = useState([]);
    const [loading, setLoading] = useState(true);

    const planIds = availability?.plans;

    const filteredPlans = useMemo(() => {
        if (!Array.isArray(planIds) || !planIds.length) return [];
        const allowed = new Set(planIds);
        return allPlans.filter(
            (p) => allowed.has(p.id) && !p.timestamps?.deleted
        );
    }, [allPlans, planIds]);

    useEffect(() => {
        if (!resolvedCommunityId) {
            setLoading(false);
            return;
        }

        let cancelled = false;

        async function loadPlans() {
            try {
                const { data } = await api.get(`/community/${resolvedCommunityId}/plan`);
                if (!cancelled) setAllPlans(Array.isArray(data) ? data : []);
            } catch (e) {
                console.log(e);
            }
            if (!cancelled) setLoading(false);
        }

        setLoading(true);
        loadPlans();

        return () => {
            cancelled = true;
        };
    }, [resolvedCommunityId]);

    function handleOpenCheckout(planId) {
        if (!checkoutSlug) return;
        window.open(`https://pay.ginastee.com/${checkoutSlug}?plan=${planId}`, "_blank");
    }

    if (loading) {
        return (
            <ul className="community-plans-available-list community-plans-available-list--loading">
                {[0, 1, 2].map((key) => (
                    <li
                        className="community-plans-available-list__item community-plans-available-list__item--skeleton"
                        key={key}
                    >
                        <div className="community-plans-available-list__skeleton-content">
                            <Skeleton
                                className="community-plans-available-list__skeleton-title"
                                variant="rounded"
                                animation="wave"
                                height={28}
                            />
                        </div>
                        <div className="community-plans-available-list__action">
                            <Skeleton
                                className="community-plans-available-list__skeleton-button"
                                variant="rounded"
                                animation="wave"
                            />
                        </div>
                    </li>
                ))}
            </ul>
        );
    }

    if (filteredPlans.length === 0) {
        return null;
    }

    return (
        <ul className="community-plans-available-list">
            {filteredPlans.map((plan) => {
                const installmentCents = planInstallmentCents(plan);
                const priceFormatted = Parser.price.brl(installmentCents);
                const interval = Parser.interval.adjective(plan.interval).toLowerCase()

                return (
                    <li className="community-plans-available-list__item" key={plan.id}>
                        <div className="community-plans-available-list__content">
                            <Text.h4 className="community-plans-available-list__label" contrast={9}>
                                {plan.label}
                            </Text.h4>
                            <Text.span contrast={5} weight={300} className="community-plans-available-list__interval">
                                {t("plansAvailable.access", { interval })}
                            </Text.span>
                        </div>
                        <div className="community-plans-available-list__action">
                            <Button
                                size="small"
                                color="inverse"
                                disabled={!checkoutSlug}
                                text={t("plansAvailable.buyButton", { installments: plan.installments, price: priceFormatted })}
                                onClick={() => handleOpenCheckout(plan.id)}
                            />
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}
